import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "190";
const height = "190";
const viewBox = "0 0 190 190";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    sm: { width: 98, height: 98 },
    md: { width: 100, height: 100 },
    lg: { width: 190, height: 190 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.midnight}
            d="M134.474 118.26v55.752H55.028v-55.558c-.502.154-.888.232-1.274.348-12.436 4.214-24.911 8.428-37.348 12.681-4.055 1.392-7.879 1.16-11.47-1.314-4.751-3.287-6.412-10.091-3.477-15.04 2.125-3.596 4.596-6.998 6.952-10.478C20.114 87.446 31.816 70.28 43.52 53.075c.386-.58.811-1.121 1.313-1.663-1.545 5.104-1.506 10.169.31 15.195 1.814 5.026 5.097 8.892 9.655 11.792-4.442 6.495-8.806 12.952-13.364 19.602.541-.154.85-.193 1.12-.31 5.369-1.816 10.699-3.672 16.067-5.45 5.253-1.74 10.699-2.746 16.221-3.364 4.056-.464 8.15-.696 12.205-1.044.116 0 .232-.039.348-.078-1.893-1.16-3.863-2.203-5.678-3.48a18.843 18.843 0 01-4.673-4.755c1.545-1.044 3.128-1.971 4.596-3.093 5.175-4.06 7.802-9.395 8.072-16.006.077-1.508.54-3.054 1.197-4.408.889-1.817 2.665-2.358 4.596-2.126 1.97.232 3.206 1.508 3.747 3.325.502 1.624.618 3.325.927 4.987 1.313 7.694 5.407 13.378 12.282 17.05.193.117.386.233.695.387-2.704 3.673-6.18 6.264-10.351 8.042 7.879.503 15.719 1.237 23.289 3.21 7.532 1.97 14.87 4.6 22.44 6.959-4.48-6.573-8.922-13.03-13.402-19.603 4.48-3.093 7.802-6.998 9.54-12.178 1.699-5.181 1.583-10.323-.116-15.504.502.618 1.042 1.237 1.506 1.894 13.865 20.298 27.692 40.635 41.596 60.972 3.553 5.219 2.974 11.444-1.12 15.465-3.322 3.286-7.415 4.33-11.818 2.861-12.205-4.021-24.371-8.235-36.537-12.334-1.275-.348-2.395-.734-3.708-1.16zM100.178 43.255c-.966-.348-1.893-.735-2.897-1.044-2.51-.735-4.905-.348-7.222.773-.58.27-.85.232-1.159-.387-2.24-4.446-6.064-6.495-10.892-6.727-2.626-.116-5.29-.039-7.917-.039h-1.352c.27-.85.425-1.585.695-2.242 2.434-6.457 6.837-11.096 12.978-14.15 4.634-2.32 9.578-3.326 14.676-2.862 10.158.928 17.882 5.76 22.594 15.04.579 1.16.927 2.475 1.39 3.712.039.116 0 .232 0 .464-.347 0-.695.038-1.042.038-2.665 0-5.33-.038-7.995 0-5.253.116-9.308 2.243-11.664 7.114-.077.116-.116.194-.193.31z"
            key="key-0"
        />
        <path
            fill={colors.midnight}
            d="M66.653 40.626c10.08 0 18.114 8.119 18.153 18.326 0 9.897-8.227 18.133-18.23 18.055-9.964-.077-18.268-7.81-18.114-18.713.155-9.627 7.995-17.63 18.191-17.668zm-.038 27.063c4.789.04 8.96-4.06 8.96-8.776-.038-4.949-3.9-8.931-8.728-8.97-4.905-.038-9 3.79-9.038 8.506-.077 5.18 3.708 9.202 8.806 9.24zM140.924 58.913c-.039 9.279-7.184 17.708-17.612 18.056-10.428.348-18.036-7.578-18.693-17.012-.695-10.169 7.145-18.984 17.457-19.293 10.506-.348 18.616 7.578 18.848 18.249zm-18.114-8.97c-4.866-.038-8.922 3.905-8.96 8.622-.039 5.104 3.901 9.124 8.883 9.124 4.828 0 8.922-4.02 8.922-8.815 0-4.949-3.901-8.93-8.845-8.93z"
            key="key-1"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "404"
});
